/* eslint-disable @typescript-eslint/no-explicit-any */

import isEmpty from 'lodash/isEmpty';
import { useCallback, useContext, useEffect, useMemo, useState, type JSX } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { ArtifactTypeNameMapping } from '@/pages/UserAccount/UserRequests/lib';

import { ArtifactType, InvoiceStatus, UserRequestStatus } from '@/shared/types';

import { validateMultiplePhoneFieldsForERZ } from '@/components/Forms/Inputs/MultiplePhoneFieldForERZ';
import { Form, StepContainer, FormElement } from '@/components/old';

import { config } from '@/config/index';

import { ROUTES_BY_SERVICE_CODE } from '@/router/config';

import { ServiceFileManager } from '@/helpers/serviceFileManager';

import { ServicesContext } from '@/contexts/Services/ServicesContext';

import {
  DocumentData,
  ERZPermit,
  FormDataType,
  GetWeaponTechnicalConditionDocumentParams,
  ResponsePaymentDetails,
  UserRequest,
  UserService,
  UserServicePaymentType,
  Weapon,
  FormDocument,
} from '@/api/types';

import api from '@/api';

import {
  ApproveAndDownloadButton,
  DownloadButton,
  GoToPaymentStepButton,
  HomeButton,
  NextStepButton,
  PayButton,
  PayLaterButton,
  PreviousStepButton,
  SignButton,
} from '../../../../../../components/Forms/Buttons';
import {
  AdditionalDocumentFieldName,
  attachmentMapping,
  getServiceData,
  InsuranceDataFieldName,
  PaymentDataFieldName,
  TechnicalConditionDataFieldName,
} from '../../../../../../components/Forms/constants';
import {
  additionalDocumentsFieldNamesForServer,
  additionalDocumentsPayloadGroupMockup,
} from '../../../../../../components/Forms/Steps/AdditionalDocument/utils';
import {
  confirmationOfApplicationDataFieldNamesForServer,
  confirmationOfApplicationDataPayloadGroupMockup,
  ConfirmationOfApplicationFieldName,
} from '../../../../../../components/Forms/Steps/ConfirmationOfApplication';
import { PaymentType } from '../../../../../../components/Forms/Steps/Payment';
import {
  payloadDeclaredGroupName as placeOfResidencePayloadDeclaredGroupName,
  payloadRegisteredGroupName as placeOfResidencePayloadRegisteredGroupName,
  PlaceOfResidenceDataFieldName,
  placeOfResidenceDeclaredDataFieldNamesForServer,
  placeOfResidenceDeclaredDataPayloadGroupMockup,
  placeOfResidenceRegisteredDataFieldNamesForServer,
  placeOfResidenceRegisteredDataPayloadGroupMockup,
} from '../../../../../../components/Forms/Steps/PlaceOfResidence';
import { Loader } from '@/shared/ui';
import AuthContext from '../../../../../../contexts/Auth/AuthContext';
import ModalContext from '../../../../../../contexts/Modal/ModalContext';
import { ModalName } from '../../../../../../contexts/Modal/modalTypes';
import { ServiceAvailabilityProvider } from '../../../../../../contexts/ServiceAvailability/ServiceAvailabilityContext';
import { clearLocationState } from '../../../../../../helpers/clearLocationState';
import { formatDate } from '../../../../../../helpers/dates';
import { base64ToFile, fileToBase64 } from '../../../../../../helpers/fileHelper';
import { findAllDocumentTypesInFormState } from '../../../../../../helpers/findAllDocumentTypesInFormState';
import {
  markEmptyInputFieldWithAnError,
  removeErrorMarkFromInputField,
  validateRequiredFieldsToBeFilled,
} from '../../../../../../helpers/invalidFields';
import { StepNavigation } from '../../../../../../helpers/stepsNavigationDataFactory';
import { useArtifactLoader } from '../../../../../../hooks/useArtifactLoader';
import { useAsyncDataLoader } from '../../../../../../hooks/useAsyncDataLoader';
import { useFormDraft } from '../../../../../../hooks/useFormDraft';
import { usePaymentShortPolling } from '../../../../../../hooks/usePaymentShortPolling';
import { isObjectPermit, isObjectWeapon } from '../../../../helpers/typeGuards';
import {
  currentServiceCode,
  disallowedDocumentTypeCodesForAdditionalDocumentStep,
  insuranceStepNumber,
  serviceName,
  stepNumberToPay,
  StepsNavigationData,
  StepsNavigationDataWithoutPayment,
  totalSteps,
} from '../PermissionRenewal';

import Step1 from './Step1';
import Step10 from './Step10';
import Step11 from './Step11';
import Step12 from './Step12';
import Step13 from './Step13';
import { permitDataFieldNamesForServer, permitDataPayloadGroupMockup, Step2 } from './Step2';
import Step3 from './Step3';
import Step4 from './Step4';
import { personStatusDataDataFieldNamesForServer, personStatusDataPayloadGroupMockup, Step5 } from './Step5';
import Step6 from './Step6';
import Step7 from './Step7';
import Step8 from './Step8';
import Step9 from './Step9';

interface FormStepsProps {
  handleHiddenStep?: (step: string) => void;
  setMyObjects: (func: (steps: StepNavigation[]) => StepNavigation[]) => void;
  steps: StepNavigation[];
  onReceiveRequestId: (requestId: string) => void;
}

interface groups {
  name: string;
}

export interface StepProps {
  formData: FormDataType;
  handleInputChange: (name: string, value: unknown) => void;
}

enum ServerFieldName {
  permitType = 'permit',
  personPassportType = 'doc_type',
  gender = 'sex',
  registeredCity = 'settlement',
  registeredStreet = 'street',
}

const fieldNameForServer = {
  // PERMIT
  ...permitDataFieldNamesForServer,

  // OWNER
  Name: {
    name: 'first_name',
    group: 'owner',
  },
  lastName: {
    name: 'last_name',
    group: 'owner',
  },
  Patronymic: {
    name: 'patronymic',
    group: 'owner',
  },
  birthday: {
    name: 'birthday',
    group: 'owner',
  },
  gender: {
    name: ServerFieldName.gender,
    group: 'owner',
  },
  phones: {
    name: 'phones',
    group: 'owner',
  },
  email: {
    name: 'email',
    group: 'owner',
  },
  personPassportType: {
    name: ServerFieldName.personPassportType,
    group: 'owner',
  },
  seriaDocPesron: {
    name: 'serial',
    group: 'owner',
  },
  numberDocPesron: {
    name: 'serial',
    group: 'owner',
  },
  authDepDocPerson: {
    name: 'auth_dep',
    group: 'owner',
  },
  citizenship: {
    name: 'citizenship',
    group: 'owner',
  },
  issued: {
    name: 'issued',
    group: 'owner',
  },
  activeIssuedDate: {
    name: 'issued',
    group: 'owner',
  },
  expired: {
    name: 'expired',
    group: 'owner',
  },
  authDepCode: {
    name: 'auth_dep',
    group: 'owner',
  },
  unzr: {
    name: 'unzr',
    group: 'owner',
  },
  rnokpp: {
    name: 'rnokpp',
    group: 'owner',
  },
  birthPlace: {
    name: 'birth_place',
    group: 'owner',
  },

  // REGISTERED
  ...placeOfResidenceRegisteredDataFieldNamesForServer,

  // DECLARED
  ...placeOfResidenceDeclaredDataFieldNamesForServer,

  // PERSON_STATUS
  ...personStatusDataDataFieldNamesForServer,

  // ORG_NPU
  ...confirmationOfApplicationDataFieldNamesForServer,

  // ADDITIONAL_INFO
  ...additionalDocumentsFieldNamesForServer,
};

const noGroupInput: groups[] = [];

const FormData: {
  [key: string]: {
    [key: string]: unknown;
  };
} = {
  ...permitDataPayloadGroupMockup,
  owner: {
    last_name: null,
    first_name: null,
    patronymic: null,
    birthday: null,
    phones: null,
    email: null,
    doc_type: null,
    doc_name: null,
    serial: null,
    issued: null,
    expired: null,
    auth_dep: null,
    unzr: null,
    rnokpp: null,
    birth_place: null,
    sex: null,
    citizenship_id: null,
    citizenship_name: null,
  },
  ...placeOfResidenceRegisteredDataPayloadGroupMockup,
  ...confirmationOfApplicationDataPayloadGroupMockup,
  ...placeOfResidenceDeclaredDataPayloadGroupMockup,
  ...personStatusDataPayloadGroupMockup,
  ...additionalDocumentsPayloadGroupMockup,
  service: {
    id: null,
    name: null,
    code: null,
  },
};

const FormDataAttachments: {
  attachments?: unknown[];
} = {
  attachments: [
    {
      id: null,
      typeDoc: null,
      fileName: null,
      code: null,
      flag: 'default',
      info: {},
      encoded_contents: [],
    },
  ],
};

const FormSteps: React.FC<FormStepsProps> = ({ handleHiddenStep, setMyObjects, steps, onReceiveRequestId }) => {
  const { documentsData } = useContext(AuthContext);
  const { renderSpecificModal } = useContext(ModalContext);
  const { services, isLoading: isServicesLoading, getServiceByCode } = useContext(ServicesContext);
  const { state: locationState } = useLocation();
  const { createDraft, saveDraft, loadDraft, deleteDraft, prefillFormData } = useFormDraft();
  const navigate = useNavigate();
  const {
    data: serviceDraft,
    load: loadServiceDraft,
    isLoading: isServiceDraftLoading,
  } = useAsyncDataLoader(() => loadDraft(currentServiceCode));

  const {
    data: permitTypesList,
    load: loadPermitTypesList,
    isLoading: isPermitTypesListLoading,
  } = useAsyncDataLoader(async () => (await api.dictionary.getPermitTypes()).data);
  const {
    data: weaponInspectionData,
    load: loadWeaponTechnicalConditionDocument,
    isLoading: isTechnicalConditionLoading,
  } = useAsyncDataLoader(
    async ({ id, type }: GetWeaponTechnicalConditionDocumentParams) => {
      return (await api.erz.services.getWeaponTechnicalConditionDocument({ id, type })).data;
    },
    { defaultDataValue: null },
  );
  const {
    isLoading: isLoadingPaymentDetails,
    data: paymentDetailsData,
    load: loadPaymentDetails,
  } = useAsyncDataLoader(
    async () =>
      (currentRequestId && (await api.services.getPaymentDetails(currentRequestId)).data) ||
      ({} as ResponsePaymentDetails),
    {
      defaultDataValue: {} as ResponsePaymentDetails,
    },
  );

  const availableSteps = useMemo<StepNavigation[]>(() => steps.filter((step) => !step.hidden), [steps]);
  const passportDocument = useMemo<Nullable<DocumentData>>(() => documentsData['passport'], [documentsData]);

  const [currentUserRequest, setCurrentUserRequest] = useState<Nullable<UserRequest>>(null);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [currentRequestId, setCurrentRequestId] = useState<UserRequest['id']>('');
  const [isDisabled, setIsDisabled] = useState<boolean>(!config.signUp.testFlow);
  const [data, setData] = useState<FormDataType>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [willBePayedLater, setWillBePayedLater] = useState<boolean>(false);
  const [allowedPaymentType, setAllowedPaymentType] = useState<UserService['paymentType']>();

  const serviceFileManager = useMemo(() => new ServiceFileManager(), []);
  const isOfflinePayment = useMemo<boolean>(
    () => data[PaymentDataFieldName.paymentType] === PaymentType.OFFLINE,
    [data],
  );
  const usedDocumentTypeCodes = useMemo(() => findAllDocumentTypesInFormState(data), [data]);
  const isInsuranceStepMandatory = false;
  const isNextStepButtonDisabled = useMemo<boolean>(
    () =>
      (currentStep === insuranceStepNumber &&
        (isInsuranceStepMandatory || data[InsuranceDataFieldName.confirmInsuranceDoc]) &&
        !(data[InsuranceDataFieldName.confirmInsuranceDocuments] as File[])?.length) as boolean,
    [currentStep, data, isInsuranceStepMandatory],
  );

  const { isArtifactLoading, downloadArtifact } = useArtifactLoader((artifactType) =>
    currentRequestId ? api.downloadUserRequestArtifact(currentRequestId, artifactType) : null,
  );
  const { isShortPollingSuccessed, isShortPollingInProgress, isShortPollingFailed, startPaymentShortPolling } =
    usePaymentShortPolling(setCurrentUserRequest);

  const shouldPaymentBeAccessible = data['shouldPaymentBeAccessible'] ?? true;

  const stepNumberToSubmitForm = StepsNavigationData.length - 2;
  const stepNumberToDownloadPdf = StepsNavigationData.length - 3;

  const scrollTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  const handleRequestIdChange = (requestId: string): void => {
    setCurrentRequestId(requestId);
    onReceiveRequestId(requestId);
  };

  useEffect(() => {
    // Redirect from GERC
    if (!!locationState?.continueGercPayment && !!locationState?.request_id) {
      setCurrentStep(stepNumberToPay);
      handleRequestIdChange(locationState.request_id);
      startPaymentShortPolling(locationState.request_id);
    }
    // Redirect from UserRequests.active by pressing button "Оплатити"
    if (!!locationState?.continuePayment && !!locationState?.request_id) {
      setCurrentStep(stepNumberToPay);
      handleRequestIdChange(locationState.request_id);
    }
    // Redirect from UserRequests.draft by pressing button "Продовжити"
    if (!!locationState?.continueDraft && !!locationState?.request_id) {
      setIsLoading(true);
      handleRequestIdChange(locationState.request_id);

      prefillFormData({
        draftId: locationState.request_id,
        handleInputChange: handleInputChange,
      })
        .then(({ meta, parsedStateForm }) => {
          setData(parsedStateForm);
          scrollTop();
          meta?.current_step && setCurrentStep(meta.current_step);
          return loadServiceDraft();
        })
        .finally(() => setIsLoading(false));
    }

    clearLocationState();
  }, []);

  useEffect(() => {
    if (
      data['shouldPaymentBeAccessible'] === true ||
      !Object.prototype.hasOwnProperty.call(data, 'shouldPaymentBeAccessible')
    )
      return;

    setMyObjects((prev) => {
      return StepsNavigationDataWithoutPayment.map((step) => {
        const old = prev.find((p) => p.step === step.step);
        return {
          ...step,
          active: old?.active ?? step.active,
          completed: old?.completed ?? step.completed,
        } as StepNavigation;
      });
    });
  }, [data]);

  useEffect(() => {
    if (currentStep === stepNumberToPay && isEmpty(currentUserRequest)) {
      (async (): Promise<void> => {
        if (currentRequestId) {
          setIsLoading(true);

          const { data: userRequest } = await api.services.draft.getDetails(currentRequestId);

          setIsLoading(false);
          setCurrentUserRequest(userRequest);
        }
      })();
    }
  }, [currentStep, currentUserRequest, currentRequestId]);

  useEffect(() => {
    if (isShortPollingSuccessed || isShortPollingFailed) {
      setCurrentStep((prevStep) => prevStep + 1);
    }
  }, [isShortPollingSuccessed, isShortPollingFailed]);

  useEffect(() => {
    scrollTop();

    if (!FormData.service.id) {
      getServiceData(currentServiceCode, getServiceByCode(currentServiceCode))
        .then(({ service, applicationAttachment }) => {
          FormData.service = service;
          if (FormDataAttachments.attachments) {
            FormDataAttachments.attachments[0] = applicationAttachment;
          }
        })
        .catch((error) => {
          if (error.status === 403 || error.status === 401) {
            getServiceData(currentServiceCode, getServiceByCode(currentServiceCode));
          }
        });
    }
  }, [scrollTop]);

  useEffect(() => {
    if (data?.permitType) {
      const permit = JSON.parse(data?.permitType.toString());
      handleHiddenStep && handleHiddenStep(permit.permitType);
    }
  }, [data, handleHiddenStep]);

  useEffect(() => {
    loadPermitTypesList();
  }, []);

  useEffect(() => {
    setAllowedPaymentType(services?.find((service) => service.code === currentServiceCode)?.paymentType);
  }, [services]);

  const handlePayButtonClick = async (): Promise<void> => {
    setIsLoading(true);
    try {
      if (currentRequestId) {
        const { redirect_uri } = await api.services.draft.createInvoice(currentRequestId);
        window.location.href = redirect_uri;
      }
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  };

  const handleClearField = useCallback((name: string) => {
    setData((prevFormData) => ({
      ...prevFormData,
      [name]: null,
    }));
  }, []);

  const handleInputChange = (name: string, value: unknown): void => {
    const isFieldInGroup = Object.keys(fieldNameForServer).includes(name);

    if (!isFieldInGroup) {
      if (
        !noGroupInput.some((x) => {
          return x.name === name;
        })
      ) {
        noGroupInput.push({
          name: name,
        });
      }
    }

    if (value === null || (value instanceof Date && isNaN(value as any))) {
      setData((prevFormData) => ({
        ...prevFormData,
        [name]: null,
      }));
    } else if (typeof value === 'string' || typeof value === 'boolean') {
      setData((prevFormData) => ({
        ...prevFormData,
        [name]: value,
      }));
    } else if (typeof value === 'object') {
      if (value instanceof Array) {
        setData((prevFormData) => ({
          ...prevFormData,
          [name]: value,
        }));
      } else {
        const stringValue = JSON.stringify(value).replace(/^"(.*)"$/, '$1');
        setData((prevFormData) => ({
          ...prevFormData,
          [name]: stringValue,
        }));
      }
    }

    removeErrorMarkFromInputField(name);
  };

  const weaponDataValue = data['permitType'];

  const weaponData = useMemo<Nullable<Weapon | ERZPermit>>(() => {
    const weaponRawData = data['permitType'] as Weapon | ERZPermit | string;

    if (typeof weaponRawData === 'string') {
      try {
        return JSON.parse(weaponRawData) as Weapon;
      } catch (err) {
        return null;
      }
    }
    return weaponRawData || null;
  }, [weaponDataValue]);

  useEffect(() => {
    if (!weaponInspectionData) return;

    handleInputChange(
      TechnicalConditionDataFieldName.confirmTechnicalConditionDocSerialNumber,
      weaponInspectionData.number,
    );
    handleInputChange(
      TechnicalConditionDataFieldName.confirmTechnicalConditionDocIssuingAuthority,
      weaponInspectionData.issued_by,
    );
    handleInputChange(
      TechnicalConditionDataFieldName.confirmTechnicalConditionDocIssueDate,
      weaponInspectionData.issued,
    );

    base64ToFile(weaponInspectionData.file ?? '').then((file) => {
      handleInputChange(TechnicalConditionDataFieldName.confirmTechnicalConditionDocument, [file]);
    });
  }, [weaponInspectionData]);

  const getWeaponTechnicalConditionDocument = (): void => {
    if (isObjectWeapon(weaponData) && weaponData.weaponType !== 'cold_steel') {
      loadWeaponTechnicalConditionDocument({
        id: weaponData.id,
        type: weaponData.weaponType,
      });
      return;
    }
    if (isObjectPermit(weaponData)) {
      const [weapon] = weaponData.objects;

      if (!weapon || !weapon.type) return;

      loadWeaponTechnicalConditionDocument({
        id: weapon.id,
        type: weapon.type,
      });
      return;
    }
  };

  const handleNextStep = async (): Promise<void> => {
    if (isLoading) return;

    if (currentStep === 1) {
      if (isEmpty(passportDocument)) {
        renderSpecificModal({
          modalName: ModalName.DocumentMissingAlert,
          modalProps: {
            serviceName,
            navigate,
          },
        });

        return;
      }

      try {
        setIsLoading(true);

        const draftId = await createDraft({
          id: FormData.service.id as string,
          name: FormData.service.name as string,
          code: currentServiceCode,
          totalSteps,
        });

        handleRequestIdChange(draftId);

        await loadServiceDraft();
      } catch (error) {
        const parsedError = JSON.parse(error as string);
        const { requestId, reason } = JSON.parse(parsedError.message) as {
          requestId: number;
          reason: UserRequestStatus.DRAFT | UserRequestStatus.PAYMENT;
        };

        switch (reason) {
          case UserRequestStatus.DRAFT:
            renderSpecificModal({
              modalName: ModalName.ServiceDraftExistingAlert,
              modalProps: {
                startNewDraft: () => {
                  setIsLoading(true);

                  deleteDraft(`${requestId}`)
                    .then(async () => {
                      setData({});
                      await handleNextStep(); // createDraft logic again
                    })
                    .finally(() => setIsLoading(false));
                },
                continueDraft: () => {
                  setIsLoading(true);

                  prefillFormData({
                    draftId: `${requestId}`,
                    handleInputChange: handleInputChange,
                  })
                    .then(({ meta, parsedStateForm }) => {
                      setData(parsedStateForm);
                      scrollTop();
                      handleRequestIdChange(`${requestId}`);
                      meta?.current_step && setCurrentStep(meta.current_step);
                      return loadServiceDraft();
                    })
                    .finally(() => setIsLoading(false));
                },
              },
            });
            return;

          case UserRequestStatus.PAYMENT: {
            renderSpecificModal({
              modalName: ModalName.PayOrLeaveAlert,
              modalProps: {
                payButtonAction: () => {
                  scrollTop();
                  setCurrentRequestId(`${requestId}`);
                  setCurrentStep(stepNumberToPay);
                },
                // TODO: make function to delete request in payment status after back implements endpoint
                onDeleteCallback: () => {
                  setIsLoading(true);

                  deleteDraft(`${requestId}`)
                    .then(async () => {
                      setData({});
                      await handleNextStep(); // createDraft logic again
                    })
                    .finally(() => setIsLoading(false));
                },
              },
            });
            return;
          }

          default:
            console.error(error);
            break;
        }
      } finally {
        setIsLoading(false);
      }

      scrollTop();
      setCurrentStep((prevStep) => prevStep + 1);
      return;
    }

    const hasInvalidPhoneFields = currentStep === 3 ? !(await validateMultiplePhoneFieldsForERZ()) : false;
    const { hasBlankFields, blankInputFields } = validateRequiredFieldsToBeFilled(data);
    const hasValidationErrors = hasBlankFields || hasInvalidPhoneFields;

    hasBlankFields && blankInputFields.forEach(markEmptyInputFieldWithAnError);

    if (!hasValidationErrors) {
      if (currentStep < stepNumberToDownloadPdf) {
        setIsLoading(true);

        try {
          if (serviceDraft?.id) {
            const activeStep = steps.find((x) => {
              return x.step > currentStep && !x.hidden;
            });

            await saveDraft({
              draftId: serviceDraft.id,
              draftCurrentStep: serviceDraft?.meta?.current_step || 0,
              meta: {
                current_step: activeStep?.step || currentStep + 1,
                total_steps: availableSteps.length,
              },
              stateForm: data,
            });

            await loadServiceDraft();

            activeStep?.step && setCurrentStep(activeStep.step);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error);
        } finally {
          setIsLoading(false);
          scrollTop();
        }
      }
      if (currentStep === stepNumberToPay && isOfflinePayment) {
        setIsLoading(true);

        try {
          if (currentRequestId) {
            const [receiptBase64] = await fileToBase64(data[PaymentDataFieldName.paymentDocument] as [File]);

            await api.services.attachReceipt(currentRequestId, receiptBase64);
            await loadServiceDraft();

            setWillBePayedLater(false);
            setCurrentStep((prevStep) => prevStep + 1);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error);
        } finally {
          setIsLoading(false);
        }
      }
    }
  };

  const handlePrevStep = (): void => {
    if (currentStep > 1) {
      scrollTop();
      const activeStep = steps.find((x) => {
        return x.step === currentStep - 1;
      });
      setCurrentStep(activeStep?.hidden ? activeStep.step - 1 : (activeStep?.step as number));
    }
  };

  const StepButtons = function (): Nullable<JSX.Element> {
    const orgNpu = data['orgNpu'];
    const checkbox = data['allowToDataProcessing'];

    let containerBtn = null;

    if (currentStep === 1) {
      containerBtn = <NextStepButton handleNextStep={handleNextStep} />;
    } else if (currentStep > 1 && currentStep < stepNumberToDownloadPdf) {
      containerBtn = (
        <>
          <PreviousStepButton handlePrevStep={handlePrevStep} />
          <NextStepButton handleNextStep={handleNextStep} disabled={isNextStepButtonDisabled} />
        </>
      );
    } else if (currentStep === stepNumberToDownloadPdf) {
      containerBtn = (
        <>
          <PreviousStepButton handlePrevStep={handlePrevStep} />
          <ApproveAndDownloadButton
            disabled={!orgNpu || !checkbox}
            onClick={(): void => {
              setIsLoading(true);
              create_peyload();
            }}
          />
        </>
      );
    } else if (currentStep === stepNumberToSubmitForm) {
      containerBtn = (
        <SignButton
          disabled={isDisabled || isLoading}
          onClick={(): void => {
            setIsLoading(true);
            create_peyload();
          }}
        />
      );
    } else if (currentStep === stepNumberToPay && shouldPaymentBeAccessible) {
      if (!isShortPollingInProgress) {
        containerBtn = (
          <>
            <PayLaterButton
              onClick={(): void => {
                setWillBePayedLater(true);
                setCurrentStep((prevStep) => prevStep + 1);
              }}
            />
            {data[PaymentDataFieldName.paymentType] === PaymentType.ONLINE ? (
              <PayButton onClick={handlePayButtonClick} />
            ) : (
              <NextStepButton disabled={!data[PaymentDataFieldName.paymentDocument]} handleNextStep={handleNextStep} />
            )}
          </>
        );
      }
    } else {
      switch (true) {
        case allowedPaymentType !== UserServicePaymentType.WITHOUT_PAYMENT &&
          !isOfflinePayment &&
          currentUserRequest?.invoice?.status === InvoiceStatus.SUCCESS:
          containerBtn = (
            <>
              <DownloadButton
                downloading={isArtifactLoading}
                text={ArtifactTypeNameMapping[ArtifactType.RECEIPT]}
                onClick={(): Promise<void> => downloadArtifact(ArtifactType.RECEIPT, currentServiceCode)}
                outline
                place="left-side"
              />
              <HomeButton />
            </>
          );
          break;

        case allowedPaymentType !== UserServicePaymentType.WITHOUT_PAYMENT &&
          !isOfflinePayment &&
          currentUserRequest?.invoice?.status === InvoiceStatus.IN_PROGRESS:
        case allowedPaymentType !== UserServicePaymentType.WITHOUT_PAYMENT &&
          !isOfflinePayment &&
          currentUserRequest?.invoice?.status === InvoiceStatus.FAILED:
        case willBePayedLater:
          containerBtn = (
            <>
              <GoToPaymentStepButton
                disabled={!willBePayedLater && isShortPollingFailed}
                onClick={(): void => {
                  setWillBePayedLater(false);
                  setCurrentStep((prevStep) => prevStep - 1);
                }}
              />
              <HomeButton />
            </>
          );
          break;

        default:
          containerBtn = <HomeButton />;
          break;
      }
    }

    return !!containerBtn ? <FormElement variant="bottom">{containerBtn}</FormElement> : <></>;
  };

  const renderFormStep = (step: number): Nullable<JSX.Element> => {
    if (shouldPaymentBeAccessible) {
      switch (step) {
        case 1:
          return <Step1 />;
        case 2:
          return (
            <Step2 formData={data} handleInputChange={handleInputChange} permitTypesList={permitTypesList ?? []} />
          );
        case 3:
          return (
            <Step3 handleClearInput={handleClearField} formData={data} handleInputChange={handleInputChange} isERZ />
          );
        case 4:
          return <Step4 formData={data} handleClearInput={handleClearField} handleInputChange={handleInputChange} />;
        case 5:
          return <Step5 formData={data} handleClearInput={handleClearField} handleInputChange={handleInputChange} />;

        case 6:
          return <Step6 formData={data} handleClearInput={handleClearField} handleInputChange={handleInputChange} />;
        case 7:
          return (
            <Step7
              formData={data}
              isStepMandatory={isInsuranceStepMandatory}
              handleClearInput={handleClearField}
              handleInputChange={handleInputChange}
              serviceLink={ROUTES_BY_SERVICE_CODE[currentServiceCode]}
              requestId={currentRequestId}
            />
          );
        case 8:
          return (
            <Step8
              formData={data}
              isTechnicalConditionLoading={isTechnicalConditionLoading}
              handleClearInput={handleClearField}
              handleInputChange={handleInputChange}
              getWeaponTechnicalConditionDocument={getWeaponTechnicalConditionDocument}
            />
          );
        case 9:
          return (
            <Step9
              formData={data}
              handleClearInput={handleClearField}
              handleInputChange={handleInputChange}
              disallowedDocumentTypeCodes={[
                ...usedDocumentTypeCodes,
                ...disallowedDocumentTypeCodesForAdditionalDocumentStep,
              ]}
            />
          );

        case 10:
          return (
            <Step10 formData={data} handleInputChange={handleInputChange} permitTypesList={permitTypesList ?? []} />
          );

        case 11:
          return (
            <Step11
              fileManager={serviceFileManager}
              rnokpp={data.rnokpp as string}
              onSignComplete={() => setIsDisabled(false)}
            />
          );

        case 12:
          return (
            <Step12
              data={paymentDetailsData}
              isLoading={isLoadingPaymentDetails}
              loadPaymentDetails={loadPaymentDetails}
              formData={data}
              isPaymentInProgress={isShortPollingInProgress}
              handleClearInput={handleClearField}
              handleInputChange={handleInputChange}
              currentRequestId={currentRequestId}
              allowedPaymentType={allowedPaymentType}
            />
          );

        case 13:
          return (
            <Step13
              willBePayedLater={willBePayedLater}
              invoiceStatus={isOfflinePayment ? null : currentUserRequest?.invoice?.status}
              serviceCode={currentServiceCode}
            />
          );

        default:
          return null;
      }
    }

    switch (step) {
      case 1:
        return <Step1 />;
      case 2:
        return <Step2 formData={data} handleInputChange={handleInputChange} permitTypesList={permitTypesList ?? []} />;
      case 3:
        return <Step3 handleClearInput={handleClearField} formData={data} handleInputChange={handleInputChange} />;
      case 4:
        return <Step4 formData={data} handleClearInput={handleClearField} handleInputChange={handleInputChange} />;
      case 5:
        return <Step5 formData={data} handleClearInput={handleClearField} handleInputChange={handleInputChange} />;

      case 6:
        return <Step6 formData={data} handleClearInput={handleClearField} handleInputChange={handleInputChange} />;
      case 7:
        return (
          <Step7
            formData={data}
            isStepMandatory={isInsuranceStepMandatory}
            handleClearInput={handleClearField}
            handleInputChange={handleInputChange}
            serviceLink={ROUTES_BY_SERVICE_CODE[currentServiceCode]}
            requestId={currentRequestId}
          />
        );
      case 8:
        return (
          <Step8
            formData={data}
            isTechnicalConditionLoading={isTechnicalConditionLoading}
            handleClearInput={handleClearField}
            handleInputChange={handleInputChange}
            getWeaponTechnicalConditionDocument={getWeaponTechnicalConditionDocument}
          />
        );
      case 9:
        return (
          <Step9
            formData={data}
            handleClearInput={handleClearField}
            handleInputChange={handleInputChange}
            disallowedDocumentTypeCodes={[
              ...usedDocumentTypeCodes,
              ...disallowedDocumentTypeCodesForAdditionalDocumentStep,
            ]}
          />
        );

      case 10:
        return <Step10 formData={data} handleInputChange={handleInputChange} permitTypesList={permitTypesList ?? []} />;

      case 11:
        return (
          <Step11
            fileManager={serviceFileManager}
            rnokpp={data.rnokpp as string}
            onSignComplete={() => setIsDisabled(false)}
          />
        );

      case 12:
        return (
          <Step13
            willBePayedLater={willBePayedLater}
            invoiceStatus={isOfflinePayment ? null : currentUserRequest?.invoice?.status}
            serviceCode={currentServiceCode}
          />
        );

      default:
        return null;
    }
  };

  function create_peyload(): void {
    if (FormDataAttachments.attachments) {
      FormDataAttachments.attachments = [FormDataAttachments.attachments[0]];
    }

    async function createFormDate(): Promise<void> {
      async function createFileBase64(files: File[]): Promise<string[]> {
        const arrFiles: string[] = [];
        if (currentStep !== stepNumberToDownloadPdf) {
          for (let f = 0; f < files.length; f++) {
            const reader = new FileReader();
            const filePromise = new Promise<string>((resolve) => {
              reader.onload = function (): void {
                // eslint-disable-next-line @typescript-eslint/no-shadow
                const base64String = btoa(reader.result as string);
                resolve(base64String.toString());
              };
            });
            reader.readAsBinaryString(files[f]);
            const base64String = await filePromise;
            arrFiles.push(base64String);
          }
        }
        return arrFiles;
      }

      function createInfo(
        info: Record<string, string>,
        // eslint-disable-next-line @typescript-eslint/no-shadow
        data: Record<string, unknown>,
      ): Record<string, unknown> {
        const obj: Record<string, unknown> = {};

        Object.keys(info).forEach((key) => {
          const infoKey = info[key];
          if (data[infoKey]) {
            obj[key] = formatDate(data[infoKey] as string, '-', true);
          }
        });
        return obj;
      }

      function createAdditionalDocumentInfo(info?: Record<string, string>): Record<string, unknown> {
        const obj: Record<string, unknown> = {};

        info &&
          Object.entries(info).forEach(([key, value]) => {
            obj[key] = formatDate(value as string, '-', true);
          });

        return obj;
      }

      for (const i in data) {
        if (Object.hasOwnProperty.call(data, i)) {
          const serverName = (
            fieldNameForServer as {
              [key: string]: { name: string; group: string };
            }
          )[i];

          if (serverName && data[i]) {
            const formDataValue = data[i];
            const jsonString = typeof formDataValue === 'string' ? formDataValue : JSON.stringify(formDataValue);
            const group = serverName.group;
            const name = serverName.name;
            const id_key = name + '_id';
            const name_key = name + '_name';

            if (!group || !name) {
              return;
            }

            let obj;
            try {
              obj = JSON.parse(jsonString);
            } catch (e) {}

            if (obj && typeof obj === 'object') {
              if (obj instanceof Object) {
                if (name === ServerFieldName.permitType) {
                  FormData[group].id = obj.id;
                  FormData[group].type = obj.permitType;
                } else if (name === ServerFieldName.personPassportType) {
                  FormData[group].doc_type = obj.code;
                  FormData[group].doc_name = obj.label;
                } else if (name === ServerFieldName.gender) {
                  FormData[group].sex = obj.id;
                } else {
                  if (FormData[group][id_key] === undefined) {
                    FormData[group].id = obj.id;
                    FormData[group].name = obj.label;
                  } else {
                    FormData[group][id_key] = obj.id;
                    FormData[group][name_key] = obj.label;
                  }
                }
              }
              if (obj instanceof Array) {
                FormData[group][name] = obj;
              }
            } else {
              if (name === ServerFieldName.registeredCity || name === ServerFieldName.registeredStreet) {
                FormData[group][id_key] = null;
                FormData[group][name_key] = data[i];
              } else {
                FormData[group][name] = formatDate(data[i]?.toString() ?? '', '-', true);
              }
            }
          } else {
            const statusGroup = noGroupInput.find((x) => {
              return x.name === i;
            });
            if (statusGroup) {
              let obj;
              try {
                obj = JSON.parse(data[i]?.toString() ?? '');
              } catch (e) {}

              if (i === AdditionalDocumentFieldName.additionalDocuments && FormDataAttachments.attachments) {
                for (const additionalAttachment of data[i] as unknown as FormDocument[]) {
                  const { type, attachments, info } = additionalAttachment;

                  FormDataAttachments.attachments.push({
                    id: type?.id,
                    typeDoc: type?.label,
                    fileName: type?.label,
                    code: type?.code,
                    info: createAdditionalDocumentInfo(info),
                    flag: 'additional',
                    encoded_contents: Array.isArray(attachments) ? await createFileBase64(attachments) : [],
                  });
                }
              }

              if (i === InsuranceDataFieldName.confirmInsuranceDocuments && FormDataAttachments.attachments) {
                for (const insuranceDocument of data[i] as unknown as FormDocument[]) {
                  const { type, attachments, info } = insuranceDocument;

                  FormDataAttachments.attachments.push({
                    id: type?.id,
                    typeDoc: type?.label,
                    fileName: type?.label,
                    code: type?.code,
                    info: createAdditionalDocumentInfo(info),
                    flag: 'default',
                    encoded_contents: Array.isArray(attachments) ? await createFileBase64(attachments) : [],
                  });
                }
              }

              if (attachmentMapping[i] && obj && FormDataAttachments.attachments) {
                const infoAttach = attachmentMapping[i].info;
                const infoContents = attachmentMapping[i].encoded_contents as string;

                FormDataAttachments.attachments.push({
                  id: obj.id,
                  typeDoc: obj.label,
                  fileName: obj.label,
                  code: obj.code,
                  addressType: obj?.addressType,
                  flag: 'default',
                  info: createInfo(infoAttach, data),
                  encoded_contents: Array.isArray(data[infoContents])
                    ? await createFileBase64(data[infoContents] as File[])
                    : [],
                });
              }
            }
          }
        }
      }

      if (!data[PlaceOfResidenceDataFieldName.registeredDoc] === true) {
        FormData[placeOfResidencePayloadDeclaredGroupName] = {
          ...FormData[placeOfResidencePayloadRegisteredGroupName],
        };
      }
    }

    async function someFunction(): Promise<void> {
      await createFormDate();
      await requestFormData();
    }

    someFunction();
    scrollTop();
  }

  async function requestFormData(): Promise<void> {
    if (currentStep === stepNumberToDownloadPdf && FormDataAttachments.attachments) {
      const FormDataAttachmentsWithoutApplication = {
        attachments: FormDataAttachments.attachments.slice(1),
      };

      api.services
        .generatePdf(currentServiceCode, {
          requestId: currentRequestId,
          ...FormData,
          ...FormDataAttachmentsWithoutApplication,
        })
        .then((response) => {
          serviceFileManager.setAttachment(response);
          setCurrentStep((prevStep) => prevStep + 1);
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error(error);
        })
        .finally(() => setIsLoading(false));
    }

    if (currentStep === stepNumberToSubmitForm) {
      if (FormDataAttachments.attachments) {
        (FormDataAttachments.attachments[0] as any).encoded_contents = await serviceFileManager.getBase64Array();
      }

      api.services
        .submit(
          currentServiceCode,
          {
            requestId: currentRequestId,
            ...FormData,
            ...FormDataAttachments,
            ...serviceFileManager.getSignature(),
          },
          String(data[ConfirmationOfApplicationFieldName.allowToProfileUpdate]),
        )
        .then(() => {
          serviceFileManager.download();
          serviceFileManager.clear();
          setCurrentStep((prevStep) =>
            allowedPaymentType === UserServicePaymentType.WITHOUT_PAYMENT ? prevStep + 2 : prevStep + 1,
          );
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error(error);
        })
        .finally(() => setIsLoading(false));
    }
  }

  return (
    <Form name="weaponPurchasePermit" className="max-width-md" isBgDefault={true}>
      <StepContainer isLoading={isLoading} data-step={currentStep}>
        <ServiceAvailabilityProvider serviceCode={currentServiceCode} skipAvailabilityValidation={currentStep === 1}>
          {isLoading || isServiceDraftLoading || isServicesLoading || isPermitTypesListLoading ? (
            <Loader centered />
          ) : (
            <>
              {renderFormStep(currentStep)}
              {StepButtons()}
            </>
          )}
        </ServiceAvailabilityProvider>
      </StepContainer>
    </Form>
  );
};

export default FormSteps;
